import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContentPage = () => {
  const [content, setContent] = useState({
    hero_tagline_fr: "C'est pas un festival. C'est notre été.",
    hero_tagline_en: "It's not a festival. It's our summer.",
    hero_tagline_nl: "Dit is geen festival. Dit is onze zomer.",
    hero_tagline_es: "No es un festival. Es nuestro verano.",
    ideology_slogan: "Respect. Sécurité. Familia.",
    about_text: "",
    founders_seba_bio: "Co-fondateur d'Invasion Latina depuis 2009",
    founders_david_bio: "Co-fondateur d'Invasion Latina depuis 2009",
    experience_intro: "",
    stats_years: "16+",
    stats_people: "5000",
    stats_hours: "8h",
    stats_tickets_per_year: "240K+"
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/content`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      if (response.data) {
        setContent({ ...content, ...response.data });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/content`, content, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Contenu sauvegardé!');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-content-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Contenu & Textes</h1>
          <p className="text-sm text-[#B9B2A8]">Modifiez les textes du site</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Taglines */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Tagline Hero (multi-langue)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Français</label>
              <Input
                value={content.hero_tagline_fr}
                onChange={(e) => setContent({ ...content, hero_tagline_fr: e.target.value })}
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">English</label>
              <Input
                value={content.hero_tagline_en}
                onChange={(e) => setContent({ ...content, hero_tagline_en: e.target.value })}
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Nederlands</label>
              <Input
                value={content.hero_tagline_nl}
                onChange={(e) => setContent({ ...content, hero_tagline_nl: e.target.value })}
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Español</label>
              <Input
                value={content.hero_tagline_es}
                onChange={(e) => setContent({ ...content, hero_tagline_es: e.target.value })}
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
          </div>
        </motion.div>

        {/* Slogan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Slogan Idéologie</h2>
          <Input
            value={content.ideology_slogan}
            onChange={(e) => setContent({ ...content, ideology_slogan: e.target.value })}
            placeholder="Respect. Sécurité. Familia."
            className="bg-white/5 border-white/10 text-[#F5F0E8]"
          />
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Statistiques</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Années</label>
              <Input
                value={content.stats_years}
                onChange={(e) => setContent({ ...content, stats_years: e.target.value })}
                placeholder="16+"
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Personnes</label>
              <Input
                value={content.stats_people}
                onChange={(e) => setContent({ ...content, stats_people: e.target.value })}
                placeholder="5000"
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Heures de musique</label>
              <Input
                value={content.stats_hours}
                onChange={(e) => setContent({ ...content, stats_hours: e.target.value })}
                placeholder="8h"
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Tickets/an</label>
              <Input
                value={content.stats_tickets_per_year}
                onChange={(e) => setContent({ ...content, stats_tickets_per_year: e.target.value })}
                placeholder="240K+"
                className="bg-white/5 border-white/10 text-[#F5F0E8]"
              />
            </div>
          </div>
        </motion.div>

        {/* Founders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Fondateurs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Bio Seba</label>
              <Textarea
                value={content.founders_seba_bio}
                onChange={(e) => setContent({ ...content, founders_seba_bio: e.target.value })}
                rows={2}
                className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Bio David</label>
              <Textarea
                value={content.founders_david_bio}
                onChange={(e) => setContent({ ...content, founders_david_bio: e.target.value })}
                rows={2}
                className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
              />
            </div>
          </div>
        </motion.div>

        {/* About Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/5 border border-white/10 rounded-xl p-6"
        >
          <h2 className="font-display text-lg text-[#F5F0E8] mb-4">Texte "À propos" (page Histoire)</h2>
          <Textarea
            value={content.about_text}
            onChange={(e) => setContent({ ...content, about_text: e.target.value })}
            rows={4}
            placeholder="Née d'une passion pour la musique latine à Bruxelles..."
            className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default AdminContentPage;
